import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { fmtDate, fmtHours, formatApiError } from "@/lib/api";
import { PageHeader, LeaveTypeBadge, Avatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const [decision, setDecision] = useState(null);

  const [sp] = useSearchParams();
  const nav = useNavigate();
  const focusId = sp.get("focus");
  const [loaded, setLoaded] = useState(false);
  const load = () => api.get("/requests", { params: { status: "pending" } }).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!focusId || !loaded) return;
    const el = document.getElementById(`approval-${focusId}`);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
    // Request is no longer pending (already approved/declined) — show its detail on the requests page.
    toast.info("This request has already been actioned.");
    nav(`/requests?focus=${focusId}`, { replace: true });
  }, [focusId, loaded, rows]);

  const act = async () => {
    try {
      if (active.group) await api.post(`/requests/group/${active.group}/${decision}`, { manager_notes: notes });
      else await api.post(`/requests/${active.single.id}/${decision}`, { manager_notes: notes });
      toast.success(decision === "approve" ? "Approved" : "Declined");
      setActive(null); setNotes(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const items = [];
  const seen = {};
  rows.forEach((b) => {
    if (b.recurrence_group) {
      if (!seen[b.recurrence_group]) {
        seen[b.recurrence_group] = { group: b.recurrence_group, key: b.recurrence_group, label: b.recurrence_label || "Series",
          employee_name: b.employee_name, employee_photo: b.employee_photo, leave_type: b.leave_type, employee_notes: b.employee_notes,
          total_hours: 0, count: 0, first: b.start_date, last: b.start_date };
        items.push(seen[b.recurrence_group]);
      }
      const g = seen[b.recurrence_group];
      g.total_hours += b.total_hours; g.count += 1;
      if (b.start_date < g.first) g.first = b.start_date;
      if (b.start_date > g.last) g.last = b.start_date;
    } else {
      items.push({ single: b, key: b.id });
    }
  });

  return (
    <div className="space-y-5">
      <PageHeader title="Holiday requests" subtitle="Review the booking impact before making a decision." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
        {rows.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400">Nothing awaiting approval 🎉</div>}
        {items.map((it) => it.single ? (() => { const b = it.single; return (
          <div key={it.key} id={`approval-${b.id}`} data-testid={`approval-card-${b.reference}`} className={`bg-white border rounded-xl p-5 shadow-sm ${focusId === b.id ? "border-[#2EA3FD] ring-2 ring-[#2EA3FD]/30" : "border-slate-200"}`}>
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3"><Avatar name={b.employee_name} photo={b.employee_photo} size={38} /><div><p className="font-semibold text-[#0B1A2F]">{b.employee_name}</p><p className="text-xs font-mono text-slate-400">{b.reference}</p></div></div>
              <LeaveTypeBadge type={b.leave_type} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
              <div><p className="text-xs text-slate-500">Dates</p><p>{fmtDate(b.start_date)}{b.start_date !== b.end_date ? ` – ${fmtDate(b.end_date)}` : ""}</p></div>
              <div><p className="text-xs text-slate-500">Hours</p><p className="tabular font-semibold">{fmtHours(b.total_hours)}</p></div>
              <div><p className="text-xs text-slate-500">Days</p><p className="tabular">{b.total_days}</p></div>
            </div>
            {b.employee_notes && <p className="text-sm text-slate-600 mt-2 italic">"{b.employee_notes}"</p>}
            {b.capacity?.exceeded ? (
              <div className="mt-3 rounded-lg border-l-4 border-l-amber-500 bg-amber-50/70 border-y border-r border-amber-200/60 p-3 text-xs text-amber-800" data-testid={`approval-capacity-${b.reference}`}>
                <p className="font-semibold mb-1">Possible coverage issue — {b.capacity.count}/{b.capacity.limit} already on annual leave. You can still approve or decline.</p>
                {b.capacity.names.map((c, i) => <div key={i}>· {c.employee_name}: {fmtDate(c.start_date)} – {fmtDate(c.end_date)} ({c.status})</div>)}
              </div>
            ) : (
              <div className="mt-3 rounded-lg border-l-4 border-l-[#2EA3FD] bg-sky-50/70 border-y border-r border-sky-200/60 p-3 text-xs text-slate-600" data-testid={`approval-noconflict-${b.reference}`}>
                <p className="font-semibold text-[#0B1A2F]">No coverage conflict</p>
                Balance and cover look fine for this period.
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <Button size="sm" data-testid={`approval-approve-button-${b.reference}`} className="bg-[#2EA3FD] hover:bg-[#1B8EE6] flex-1" onClick={() => { setActive({ single: b, label: b.reference }); setDecision("approve"); }}><Check size={15} className="mr-1" />Approve</Button>
              <Button size="sm" variant="outline" data-testid={`approval-decline-button-${b.reference}`} className="text-rose-600 border-rose-200 hover:bg-rose-50 flex-1" onClick={() => { setActive({ single: b, label: b.reference }); setDecision("decline"); }}><X size={15} className="mr-1" />Decline</Button>
            </div>
          </div>
        ); })() : (
          <div key={it.key} id={`approval-${it.group}`} data-testid={`approval-series-${it.group}`} className={`bg-white border rounded-xl p-5 shadow-sm ${focusId === it.group ? "border-[#2EA3FD] ring-2 ring-[#2EA3FD]/30" : "border-violet-200"}`}>
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3"><Avatar name={it.employee_name} photo={it.employee_photo} size={38} /><div><p className="font-semibold text-[#0B1A2F]">{it.employee_name}</p><p className="text-xs text-violet-600">Recurring series · {it.label}</p></div></div>
              <LeaveTypeBadge type={it.leave_type} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
              <div><p className="text-xs text-slate-500">Range</p><p>{fmtDate(it.first)} – {fmtDate(it.last)}</p></div>
              <div><p className="text-xs text-slate-500">Total hours</p><p className="tabular font-semibold">{fmtHours(it.total_hours)}</p></div>
              <div><p className="text-xs text-slate-500">Dates</p><p className="tabular">{it.count}</p></div>
            </div>
            {it.employee_notes && <p className="text-sm text-slate-600 mt-2 italic">"{it.employee_notes}"</p>}
            <div className="flex gap-2 mt-4">
              <Button size="sm" data-testid={`approval-approve-series-${it.group}`} className="bg-[#2EA3FD] hover:bg-[#1B8EE6] flex-1" onClick={() => { setActive({ group: it.group, label: it.label }); setDecision("approve"); }}><Check size={15} className="mr-1" />Approve all {it.count}</Button>
              <Button size="sm" variant="outline" data-testid={`approval-decline-series-${it.group}`} className="text-rose-600 border-rose-200 hover:bg-rose-50 flex-1" onClick={() => { setActive({ group: it.group, label: it.label }); setDecision("decline"); }}><X size={15} className="mr-1" />Decline all</Button>
            </div>
          </div>
        ))}
        </div>
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm lg:sticky lg:top-32" data-testid="request-summary">
            <h3 className="font-semibold text-[#0B1A2F] mb-3">Request summary</h3>
            <div className="flex items-center justify-between py-2 border-b border-slate-100"><span className="text-sm text-slate-600">Awaiting approval</span><span className="tabular font-bold text-[#0B1A2F]">{rows.length}</span></div>
            <div className="flex items-center justify-between py-2 border-b border-slate-100"><span className="text-sm text-slate-600">Recurring series</span><span className="tabular font-bold text-[#0B1A2F]">{items.filter((i) => i.group).length}</span></div>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">Approving reserves the hours against the employee's balance. Coverage warnings are advisory — you can still approve or decline with an optional note.</p>
          </div>
        </aside>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decision === "approve" ? "Approve" : "Decline"} {active?.group ? `series ${active?.label}` : `request ${active?.label}`}</DialogTitle></DialogHeader>
          <Textarea placeholder="Manager notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="decision-notes-input" />
          <DialogFooter>
            <Button data-testid="confirm-decision-btn" onClick={act} className={decision === "approve" ? "bg-[#2EA3FD] hover:bg-[#1B8EE6]" : "bg-rose-600 hover:bg-rose-700"}>
              Confirm {decision}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
